<template>
  <section :class="['wrapper', $mq, $route.query?.referrer ?? '']">
    <JoszakiLoading v-if="isLoading" active is-full-page />
    <div v-else class="wrapperInner">
      <ModularRequestTenderPage
        v-if="useModularRequestTenderPage && !isPostSubmitInactiveStep"
        :active-tab="activeTab"
        :is-loading="isLoading"
        @goto-step="goToTab"
        @submit="submitJob"
      />
      <!-- Legacy fixed-order wizard, rendered from the shared step modules.
           The v-if chain mirrors the old b-tabs destroy-on-hide behavior:
           each step component is destroyed on leave, so per-step autofocus
           re-fires on navigation. -->
      <template v-else-if="activeTab <= 7">
        <NameStepModule
          v-if="activeTab === 1"
          :current-step="currentStep"
          :total-steps="totalSteps"
          @next="onStepNext"
        >
          <template #media>
            <div class="flex flex-col mx-auto">
              <TenderHeroBlock
                v-if="personHasSelectedProfession && !noBrandActive"
                :person-public-profile="personPublicProfile"
                :last-profession="lastProfession"
                :last-city="lastCity"
                @deleteCookie="deleteCookieAndNotify"
              />
            </div>
          </template>
        </NameStepModule>

        <ProfessionStepModule
          v-else-if="activeTab === 2"
          :current-step="currentStep"
          :total-steps="totalSteps"
          @next="onStepNext"
        />

        <JobDescriptionStepModule
          v-else-if="activeTab === 3"
          :current-step="currentStep"
          :total-steps="totalSteps"
          @next="onStepNext"
        />

        <CityStepModule
          v-else-if="activeTab === 4"
          :current-step="currentStep"
          :total-steps="totalSteps"
          @next="onStepNext"
        />

        <EmailStepModule
          v-else-if="activeTab === 5"
          :current-step="currentStep"
          :total-steps="totalSteps"
          @next="onStepNext"
        />

        <PhoneStepModule
          v-else-if="activeTab === 6"
          :current-step="currentStep"
          :total-steps="totalSteps"
          @next="onStepNext"
        />

        <TermsStepModule
          v-else-if="activeTab === 7"
          :current-step="currentStep"
          :total-steps="totalSteps"
          :loading="isLoading"
          :step-descriptors="stepDescriptors"
          @submit="submitJob"
          @goto-step="goToTab"
        />
      </template>

      <!-- Step 8: post-submit inactive tradesman screen (legacy only) -->
      <div
        v-else-if="activeTab === 8 && personIsInactiveOrDoesNotHaveProfession"
      >
        <div v-if="personHasSelectedProfession" class="flex items-start">
          <div class="topCard">
            <div class="imageTop">
              <figure class="w-[72px] h-[72px] rounded-full overflow-hidden">
                <PersonPictureSet
                  :person="personPublicProfile"
                ></PersonPictureSet>
              </figure>
            </div>

            <div v-if="personPublicProfile" class="text-center">
              <i18n path="requestTender.inactiveStep.withPerson.title">
                <template #name>
                  <span class="text-2xl text-[#4a4a4a]">
                    {{ personPublicProfile.name }}
                  </span>
                </template>
                <template #titleEnd>
                  <div>
                    {{ $t("requestTender.inactiveStep.withPerson.titleEnd") }}
                  </div>
                </template>
              </i18n>
            </div>
          </div>
        </div>

        <div class="text-center">
          <template v-if="personHasSelectedProfession">
            <i18n path="requestTender.inactiveStep.personIsInactiveSince">
              <template #name>
                {{ personPublicProfile.name }}
              </template>
              <template #time>
                <time :datetime="personPublicProfile.lastLogin">
                  {{ differenceToNow(personPublicProfile.lastLogin) }}
                </time>
              </template>
            </i18n>
            <br />
            <i18n path="requestTender.inactiveStep.personMayNotAcceptJobs">
              <template #not>
                <strong>{{ $t("requestTender.inactiveStep.not") }}</strong>
              </template>
            </i18n>
          </template>
          <div v-else class="pt-8">
            {{
              $t("requestTender.inactiveStep.personDoesNotHaveProfession", {
                name: personPublicProfile.name,
              })
            }}
          </div>
          <p class="mt-4 mb-4">
            <i18n path="requestTender.inactiveStep.info1">
              <template #all>
                <span class="font-bold">{{
                  $t("requestTender.inactiveStep.all")
                }}</span>
              </template>
              <template #toActiveMates>
                <span class="font-bold">{{
                  $t("requestTender.inactiveStep.activeMates")
                }}</span>
              </template>
            </i18n>
          </p>
          <p class="mt-4 mb-4">
            {{ $t("requestTender.inactiveStep.info2") }}
          </p>
        </div>
        <div class="text-center flex flex-col items-center">
          <JoszakiButton
            type="info"
            size="xl"
            icon-left="rocket"
            @click="goToJobGatherFurtherDetails"
            @keyup.enter.exact.native="goToJobGatherFurtherDetails"
          >
            {{ $t("requestTender.inactiveStep.moreInfoCta") }}
          </JoszakiButton>
          <div>{{ $t("requestTender.inactiveStep.moreInfoCtaExample") }}</div>
        </div>
      </div>
    </div>
  </section>
</template>

<router>
  {
    name: "requestForTenderTab"
  }
</router>

<script>
import { useAsync, useContext } from "@nuxtjs/composition-api";

import { useAuthStore } from "~/components/_refactored/auth";

import { AnalyticsError } from "~/helpers/error";
import { differenceToNow } from "~/helpers/date";

import TenderHeroBlock from "~/components/_refactored/blocks/tender/TenderHeroBlock.vue";
import { useNewTender } from "~/composables/tender/useNewTender";
import useRequestTenderPageFunnel from "~/composables/useRequestTenderPageFunnel";
import {
  useTrackWizardStepViewed,
  useWizardTrackingProperties,
} from "~/composables/tracking/useTrackWizardClick";
import { useNoBrandActive } from "~/composables/useNoBrandActive";
import PersonPictureSet from "~/components/PersonPictureSet.vue";
import ModularRequestTenderPage from "~/components/_refactored/requestTenderPage/request-tender-page-modules/ModularRequestTenderPage.vue";
import {
  isFreshWizardEntry,
  resetAutoSkipForEntry,
} from "~/components/_refactored/requestTenderPage/request-tender-page-modules/autoSkipGuard";
import {
  readStoredTenderOrigin,
  resolveTenderOrigin,
  storeTenderOrigin,
} from "~/helpers/tenderOrigin";
import NameStepModule from "~/components/_refactored/requestTenderPage/request-tender-page-modules/NameStepModule.vue";
import ProfessionStepModule from "~/components/_refactored/requestTenderPage/request-tender-page-modules/ProfessionStepModule.vue";
import JobDescriptionStepModule from "~/components/_refactored/requestTenderPage/request-tender-page-modules/JobDescriptionStepModule.vue";
import CityStepModule from "~/components/_refactored/requestTenderPage/request-tender-page-modules/CityStepModule.vue";
import EmailStepModule from "~/components/_refactored/requestTenderPage/request-tender-page-modules/EmailStepModule.vue";
import PhoneStepModule from "~/components/_refactored/requestTenderPage/request-tender-page-modules/PhoneStepModule.vue";
import TermsStepModule from "~/components/_refactored/requestTenderPage/request-tender-page-modules/TermsStepModule.vue";
import { useUserStore } from "~/stores/user";

const IS_MODULAR_REQUEST_TENDER_PAGE_ENABLED =
  process.env.ENABLE_MODULAR_REQUEST_TENDER_PAGE === "true";

const MODULAR_REQUEST_TENDER_PAGE_USER_IDS = [1188288, 32480, 235006, 571754];

/**
 * The modular flow's real step count comes from the conductor config, which
 * is resolved asynchronously — the route middleware can't know it. This is
 * only a sanity bound for the URL param; ModularRequestTenderPage restarts
 * the wizard when the resolved sequence turns out to be shorter.
 */
const MODULAR_MAX_TAB = 30;

function isModularRequestTenderPage(route, pinia) {
  if (IS_MODULAR_REQUEST_TENDER_PAGE_ENABLED) return true;
  // Escape hatch for testing: any ?modular=1 in the URL enables it.
  if (route?.query?.modular === "1") return true;
  try {
    const userId = parseInt(useUserStore(pinia).me?.id);
    return MODULAR_REQUEST_TENDER_PAGE_USER_IDS.includes(userId);
  } catch (e) {
    // The user store may not be initialized yet (e.g. in SSR middleware) —
    // fall back to the legacy bounds in that case.
    return false;
  }
}

/**
 * Fixed legacy step order. The modular flow resolves its sequence from the
 * conductor config instead; this page keeps its own switcher because the
 * legacy order (name first) differs from the modular one (profession first).
 */
const LEGACY_STEPS = [
  { index: 1, fieldName: "name", slug: "name" },
  { index: 2, fieldName: "profession", slug: "profession" },
  { index: 3, fieldName: "jobDescription", slug: "job-description" },
  { index: 4, fieldName: "city", slug: "city" },
  { index: 5, fieldName: "email", slug: "email" },
  { index: 6, fieldName: "phone", slug: "phone" },
  { index: 7, fieldName: "terms", slug: "terms" },
];

export default {
  name: "TenderPage",
  components: {
    TenderHeroBlock,
    PersonPictureSet,
    ModularRequestTenderPage,
    NameStepModule,
    ProfessionStepModule,
    JobDescriptionStepModule,
    CityStepModule,
    EmailStepModule,
    PhoneStepModule,
    TermsStepModule,
  },
  middleware(ctx) {
    const { params, query, redirect, localePath } = ctx;

    // Re-arm the modular wizard's one-shot auto-skip whenever the user ENTERS
    // the wizard from another route (a fresh tender from a professional's
    // page, a direct link, etc.), so a profession/city supplied via the URL is
    // auto-skipped again. Stepping WITHIN the wizard (same route name,
    // different activeTab) is left untouched, so browser-back to a prefilled
    // step still lets the user edit it instead of being bounced forward. Full
    // page loads re-arm for free (fresh module scope), so this only needs to
    // cover client-side navigation — hence the process.client guard (and so we
    // never touch the shared module flag during SSR).
    if (process.client && isFreshWizardEntry(ctx.from?.name, ctx.route?.name)) {
      resetAutoSkipForEntry();
    }

    const activeTab = parseInt(params.activeTab || "0");
    // The legacy wizard has a fixed 7 steps (8 is the post-submit screen,
    // only reachable with a personId); the modular flow's length depends on
    // the conductor config, so it only gets the sanity bound here.
    const lastTab = isModularRequestTenderPage(ctx.route, ctx.$pinia)
      ? MODULAR_MAX_TAB
      : query.personId
      ? 8
      : 7;

    // Redirect from invalid step
    if (!activeTab || activeTab < 1 || activeTab > lastTab) {
      redirect(
        localePath({
          name: ctx.route.name.slice(0, ctx.route.name.indexOf("_")),
          query: { ...ctx.route.query },
          params: { ...ctx.route.params, activeTab: 1 },
        })
      );
    }
  },
  setup() {
    const { $logger } = useContext();
    const newTender = useNewTender();

    useAsync(async () => {
      try {
        if (!newTender.state.prefilled) {
          // avoid continously refilling with the same data
          await newTender.prefillWithIds();
        }
      } catch (error) {
        $logger.error("Error initializing new tender", error);
      }
    });

    const funnel = useRequestTenderPageFunnel();
    const { getWizardTrackingProperties } = useWizardTrackingProperties();
    const { trackWizardStepViewed } = useTrackWizardStepViewed();

    return {
      authStore: useAuthStore(),
      newTender,
      noBrandActive: useNoBrandActive(),
      trackFunnelSubmit: funnel.trackSubmit,
      getWizardTrackingProperties,
      trackWizardStepViewed,
    };
  },
  fetchOnServer: false,
  data() {
    return {
      activeTab: parseInt(this.$route.params.activeTab),
      tenderId: this.$route.query.munka,
      isLoading: false,
    };
  },
  head() {
    return {
      meta: [
        {
          name: "robots",
          content: "noindex",
        },
      ],
    };
  },
  computed: {
    useModularRequestTenderPage() {
      return isModularRequestTenderPage(this.$route, this.$pinia);
    },
    wizardTrackingProperties() {
      return this.getWizardTrackingProperties();
    },
    legacyStep() {
      return LEGACY_STEPS.find((step) => step.index === this.activeTab) || null;
    },
    personId() {
      return this.$route.query.personId;
    },
    // Step 8 is overloaded: in the legacy flow it's the post-submit
    // "inactive tradesman" screen, but in the modular flow it's a regular
    // wizard step (the terms step lands there once enough middle modules are
    // configured). Only treat step 8 as the legacy inactive screen when we
    // actually have a submitted tender (`munka` query → `tenderId`); otherwise
    // let the modular renderer own it, so terms stays reachable.
    isPostSubmitInactiveStep() {
      return this.activeTab === 8 && !!this.tenderId;
    },
    personPublicProfile() {
      if (!this.newTender.state.prefilled) {
        return null;
      }
      return this.newTender.state.mate;
    },
    personHasSelectedProfession() {
      return this.newTender.personHasSelectedProfession.value;
    },
    lastProfession() {
      if (
        this.$route.query.professionId &&
        this.newTender.profession.value?.id === this.$route.query.professionId
      ) {
        return this.newTender.profession.value;
      }
      return null;
    },
    lastCity() {
      if (
        this.$route.query.cityId &&
        this.newTender.city.value?.id === this.$route.query.cityId
      ) {
        return this.newTender.city.value;
      }
      return null;
    },
    stepDescriptors() {
      return LEGACY_STEPS;
    },
    currentStep() {
      let currentStep = this.activeTab;

      // Account for fewer steps due to profession (2) and city (4) preselected
      if (this.lastProfession && this.lastCity) {
        [2, 4].forEach((stepIndex) => {
          if (stepIndex < this.activeTab) {
            currentStep -= 1;
          }
        });
      }

      return currentStep;
    },
    totalSteps() {
      let totalSteps = 7;

      // Account for fewer steps due to profession and city preselected
      if (this.lastProfession && this.lastCity) {
        totalSteps -= 2;
      }

      return totalSteps;
    },
    hasUnansweredQuestions() {
      const answered = [
        "name",
        "profession",
        "jobDescription",
        "city",
        "email",
      ].every((fieldName) => !!this.newTender[fieldName].value);
      return !answered || !this.newTender.phone.value.number;
    },
    personIsInactiveOrDoesNotHaveProfession() {
      return (
        this.personPublicProfile &&
        (!this.personPublicProfile.isActive ||
          !this.personHasSelectedProfession)
      );
    },
  },
  mounted() {
    this.newTender.loadFromStorage();

    const tenderOrigin = resolveTenderOrigin({
      queryFromUrl: this.$route.query.fromUrl,
      storedFromUrl: this.newTender.formData.fromUrl || readStoredTenderOrigin(),
      fromRoute: this.$nuxt?.context?.from,
      toRoute: this.$route,
      origin: window.location.origin,
      currentHref: window.location.href,
      referrer: document.referrer,
    });
    this.newTender.setFromUrl(tenderOrigin);
    storeTenderOrigin(tenderOrigin);

    this.transitionFromCookie();

    if (this.activeTab === 8 && !this.useModularRequestTenderPage) {
      if (this.hasUnansweredQuestions) {
        this.goToTab(7);
      }
      if (!this.tenderId) {
        this.goToTab(1);
      }
    }

    this.$trackers.pageview({
      pagename: this.$trackers.pageNames.REQUEST_TENDER_PAGE,
      pagenumber: this.activeTab,
      personId: this.$route.query?.personId ?? null,
      citySeoName: this.lastCity?.seoName ?? null,
      professionSeoName: this.lastProfession?.seoName ?? null,
      moduleVersionId: this.wizardTrackingProperties.moduleVersionId,
      wizardProfessionId: this.wizardTrackingProperties.wizardProfessionId,
    });
    if (!this.useModularRequestTenderPage && this.legacyStep) {
      this.trackWizardStepViewed({
        step: this.legacyStep.index,
        moduleSlug: this.legacyStep.slug,
        totalSteps: LEGACY_STEPS.length,
      });
    }
    this.receiveStoredJobDescription();
  },
  beforeDestroy() {
    this.$trackers.pageleave({
      pagename: this.$trackers.pageNames.REQUEST_TENDER_PAGE,
      pagenumber: this.activeTab,
    });
  },
  methods: {
    transitionFromCookie() {
      const cookie = this.$cookie.get("createJob_questions");

      if (cookie) {
        const cookieData = JSON.parse(cookie);
        if (cookieData.name?.answer) {
          this.newTender.name.value = cookieData.name.answer;
        }
        if (cookieData.profession?.answer) {
          this.newTender.profession.value = cookieData.profession.answer;
        }
        if (cookieData.jobDescription?.answer) {
          this.newTender.jobDescription.value =
            cookieData.jobDescription.answer;
        }
        if (cookieData.city?.answer) {
          this.newTender.city.value = cookieData.city.answer;
        }
        if (cookieData.email?.answer) {
          this.newTender.email.value = cookieData.email.answer;
        }
        if (cookieData.mobile?.answer && cookieData.mobile?.countryCode) {
          this.newTender.phone.value = {
            number: cookieData.mobile.answer,
            countryCode: cookieData.mobile.countryCode,
          };
        }
        this.newTender.saveToStorage();
      }
      this.$cookie.remove("createJob_questions");
    },
    receiveStoredJobDescription() {
      const description = this.$route.query?.description;

      if (description && this.activeTab === 1) {
        this.newTender.jobDescription.value = description;
        this.newTender.saveToStorage();
      }
    },
    differenceToNow(date) {
      return differenceToNow(this, date);
    },
    goToTab(index) {
      this.$router.push({
        params: { activeTab: index },
        query: Object.assign({}, this.$route.query),
      });
    },
    async goToJobGatherFurtherDetails() {
      const showKhOffer = this.$ab.isVariant("request-tender-kh-offer", "test");
      const referrer = this.$route.query?.referrer;

      await this.$router.push(
        this.localePath({
          name: "specifyTenderPage",
          params: {
            tenderId: this.tenderId,
            activeTab:
              this.personId && this.personHasSelectedProfession ? 1 : 2,
          },
          query: {
            ...(referrer ? { referrer } : {}),
            ...(showKhOffer ? { khOffer: "1" } : {}),
          },
        })
      );
    },
    onStepNext() {
      let jump = 1;

      // Skip the profession (step 2) / city (step 4) steps when the URL
      // already carries the corresponding id. Trust the URL directly while
      // prefill is in flight (so a slow network doesn't make the user
      // re-enter info they already supplied), but if prefill has FINISHED
      // and didn't populate the corresponding value, the URL id was
      // invalid — fall through to the picker step instead of skipping,
      // otherwise the user would submit with `professionId: NaN`/`cityId:
      // NaN` for a never-loaded id.
      const prefilled = this.newTender.state.prefilled;
      const professionLoaded = !!this.newTender.profession.value?.id;
      const cityLoaded = !!this.newTender.city.value?.id;

      if (
        this.activeTab === 1 &&
        this.$route.query.professionId &&
        (!prefilled || professionLoaded)
      ) {
        jump = 2;
      }

      if (
        this.activeTab === 3 &&
        this.$route.query.cityId &&
        (!prefilled || cityLoaded)
      ) {
        jump = 2;
      }

      this.goToTab(this.activeTab + jump);
    },
    async submitJob() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;

      try {
        this.$track.analyticsEvent({
          event: "submit_job",
          category: "ajanlatkeres",
          action: "Submit job",
          label: "v1.0",
        });

        this.$trackers.trackEvent({
          title: "Submit Job",
          data: {
            professionSeoName: this.newTender.profession.value.seoName,
            professionGroupSeoName:
              this.newTender.profession.value.professionGroup?.seoName,
            citySeoName: this.newTender.city.value.seoName,
            personId:
              this.personId && this.personHasSelectedProfession
                ? parseInt(this.personId)
                : null,
            personIsActive:
              this.personId && this.personHasSelectedProfession
                ? this.personPublicProfile?.isActive
                : null,
            moduleVersionId: this.wizardTrackingProperties.moduleVersionId,
            v: 1,
          },
        });

        this.$sentry.addBreadcrumb({
          category: "debug",
          message: JSON.stringify(this.newTender.getPayload()),
          level: "info",
        });

        const { tender, token } = await this.newTender.createTender(
          true,
          false
        );

        const { id: tenderId } = tender;
        this.tenderId = tenderId;

        // Funnel "submit" event — fire-and-forget; never blocks the post-
        // submit redirect or surfaces an error to the user.
        this.trackFunnelSubmit({
          moduleVersionId: this.newTender.formData.moduleVersionId || null,
          professionId: this.newTender.profession.value?.id || null,
        });

        const professionAnswer = this.newTender.profession.value;

        try {
          this.$track.analyticsEvent({
            category: "ajanlatkeres",
            action: "normal",
          });

          if (professionAnswer) {
            this.$track.customEvent({
              event: "Facebook.track",
              content: {
                eventName: "Lead",
                eventData: {
                  content_name: professionAnswer?.seoName,
                  content_category: professionAnswer?.professionGroup?.seoName,
                  content_ids: [professionAnswer.id],
                  content_type: "profession",
                },
                eventMeta: {
                  eventID: `tender_${tenderId}`,
                },
              },
            });
          }
        } catch (err) {
          console.error(err);
          this.$sentry.captureException(new AnalyticsError(err), {
            tags: {
              analytics: "rft",
              rft: "new_tender",
            },
          });
        }

        if (token) {
          await this.authStore.loginWithToken(token);
        }

        this.$trackers.trackEvent({
          title: "Job Submitted",
          data: {
            professionSeoName: this.newTender.profession.value.seoName,
            professionGroupSeoName:
              this.newTender.profession.value.professionGroup?.seoName,
            citySeoName: this.newTender.city.value.seoName,
            personId:
              this.personId && this.personHasSelectedProfession
                ? parseInt(this.personId)
                : null,
            personIsActive:
              this.personId && this.personHasSelectedProfession
                ? this.personPublicProfile?.isActive
                : null,
            moduleVersionId: this.wizardTrackingProperties.moduleVersionId,
            tenderProfessionId: tender.profession?.id ?? null,
          },
        });

        this.$track.analyticsEvent({
          event: "job_submitted",
          category: "ajanlatkeres",
          action: "Job Submitted",
          label: "v1.0",
        });

        if (this.personIsInactiveOrDoesNotHaveProfession) {
          await this.$router.push(
            this.localePath({
              name: "requestForTenderTab",
              params: {
                activeTab: 8,
              },
              query: Object.assign({}, this.$route.query, {
                munka: tenderId,
                personId: this.personPublicProfile.id,
              }),
            })
          );
        } else {
          await this.goToJobGatherFurtherDetails();
        }
      } catch (err) {
        console.error(err);

        this.$joszaki.alert({
          type: "error",
          message:
            typeof err === "string" ? err : this.$t("requestTender.error"),
        });

        this.$sentry.addBreadcrumb({
          category: "rft",
          message: "submitJob",
          level: "info",
        });
        this.$sentry.captureException(err, {
          tags: {
            rft: "submitJob",
          },
        });

        this.isLoading = false;
        this.goToTab(1);
      }
    },
    deleteCookieAndNotify() {
      this.$router.push({
        params: {
          ...this.$route.params,
          activeTab: 1,
        },
        query: null,
      });
      this.$joszaki.toast({
        message: this.$t("requestTender.removedCityAndProfession"),
        type: "success",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.topCard {
  background-color: #f1f1f1;
  margin: 4em auto 1em auto;
  padding: 3em 1em 1em 1em;
  border-radius: 1em;
  position: relative;
  min-width: 250px;
}

.imageTop {
  position: absolute;
  width: 72px;
  left: 50%;
  margin-left: -36px;
  top: -30px;
}
</style>

<style lang="scss">
html {
  height: 100%;
  background-color: #fafafa;
}

.kh {
  .step.reached {
    background-color: #00aeef !important;
  }

  .bg-primary,
  .bg-info,
  .bg-success {
    background-color: #00aeef !important;
  }

  .hover\:bg-info-dark:hover,
  .hover\:bg-success-dark:hover {
    background-color: #0e8ecb !important;
  }

  .question-big {
    color: #00aeef !important;
  }
}
</style>
